// Liste indentée en SVG natif (descendance) : lisible, imprimable, et surtout
// exportable en PDF vectoriel comme les autres variantes. forwardRef sur le <svg>.

import React, { forwardRef } from 'react';
import { Membre, Relation } from '../../types';
import { getDescendants, getRootIds, DescendantNode } from '../../utils/lineage';
import { genColor } from '../../utils/treeVisuals';
import { StylePreset, resolveBg, fullName, memberYears } from './chartCommon';

export interface ListChartProps {
  rootId: number | null;   // null = toute la famille (tous les troncs racines)
  membres: Membre[];
  relations: Relation[];
  style: StylePreset;
  isDark: boolean;
  maxGen?: number;
}

const ROW_H = 27, INDENT = 22, PAD = 20, WIDTH = 640;

const ListChart = forwardRef<SVGSVGElement, ListChartProps>(function ListChart(
  { rootId, membres, relations, style, isDark, maxGen = 4 }, ref,
) {
  const rootIds = rootId == null ? getRootIds(membres, relations) : [rootId];

  // Aplatit chaque tronc ; `seen` déduplique les personnes atteignables depuis
  // plusieurs racines (évite de lister deux fois la même sous-lignée).
  const rows: { node: DescendantNode; depth: number }[] = [];
  const seen = new Set<number>();
  const flatten = (n: DescendantNode | null, depth: number) => {
    if (!n || seen.has(n.membre.id)) return;
    seen.add(n.membre.id);
    rows.push({ node: n, depth });
    n.children.forEach(c => flatten(c, depth + 1));
  };
  rootIds.forEach(rid => flatten(getDescendants(rid, membres, relations, maxGen), 0));

  const H = PAD * 2 + Math.max(rows.length, 1) * ROW_H;
  // Encre lisible sur le fond (les couleurs de boîte du preset visent un fond coloré)
  const ink = style.forceLight ? '#1C1B17'
    : style.key === 'decoratif' ? '#3A2E3D'
    : (isDark ? '#E8F3EA' : '#14251A');
  const muted = style.forceLight ? 'rgba(28,27,23,.6)'
    : style.key === 'decoratif' ? 'rgba(58,46,61,.62)'
    : (isDark ? 'rgba(232,243,234,.6)' : 'rgba(20,37,26,.58)');

  const els: React.ReactNode[] = [];
  rows.forEach(({ node, depth }, i) => {
    const y = PAD + i * ROW_H + ROW_H / 2;
    const x = PAD + depth * INDENT;
    const m: Membre = node.membre;
    const hasChildren = node.children.length > 0;
    const dotColor = style.forceLight ? '#1C1B17' : genColor(depth);
    if (depth > 0) {
      els.push(
        <line key={`g${i}`} x1={x - 10} y1={y - ROW_H / 2} x2={x - 10} y2={y + ROW_H / 2}
          stroke={style.edge} strokeWidth={style.edgeW} opacity={0.7} />,
      );
    }
    els.push(
      <text key={`d${i}`} x={x} y={y + 4} fill={dotColor} fontFamily={style.font} fontSize={13}>
        {hasChildren ? '◆' : '•'}
      </text>,
      <text key={`n${i}`} x={x + 16} y={y + 4} fill={ink} fontFamily={style.font}
        fontSize={13.5} fontWeight={style.weight}>
        {(m.date_deces ? '† ' : '') + fullName(m)}
      </text>,
    );
    const years = memberYears(m);
    if (years) {
      const nameW = ((m.date_deces ? '† ' : '') + fullName(m)).length * 7.2;
      els.push(
        <text key={`y${i}`} x={x + 16 + nameW + 10} y={y + 4} fill={muted} fontFamily={style.font}
          fontSize={11} fontStyle={style.italicSub ? 'italic' : undefined}>{`(${years})`}</text>,
      );
    }
  });

  const bg = resolveBg(style, isDark);
  return (
    <svg ref={ref} width={WIDTH} height={Math.ceil(H)} viewBox={`0 0 ${WIDTH} ${Math.ceil(H)}`}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Liste généalogique">
      <rect x={0} y={0} width={WIDTH} height={Math.ceil(H)} fill={bg} />
      {els}
    </svg>
  );
});

export default ListChart;
